import { useState, useEffect } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { useSubscription } from '@/lib/contexts/SubscriptionContext'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/lib/contexts/AuthContext'
import { useWorkspace } from '@/lib/contexts/WorkspaceContext'
import { WorkspaceTeamProvider } from '@/lib/contexts/WorkspaceTeamContext'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from 'sonner'
import { deleteWorkspace, updateWorkspace } from '@/lib/api/workspaces'
import { PageShell } from '@/components/shared/layout'
import { SettingsTabs, SettingsBody } from '@/components/shared/settings/SettingsKit'
import { PageHeader } from '@/components/shared/layout/PageHeader'

import {
  GeneralSettingsTab,
  HashFragSettingsTab,
  ShareLinksSettingsTab,
  TeamSettingsTab,
  WorkspaceSitesTab,
  defaultWorkspaceSettings,
  type WorkspaceSettings as WorkspaceSettingsType,
} from '@/components/workspace-settings'

interface DashboardContext {
  dashboardData?: unknown
  isLoading?: boolean
  propertyId?: string
}

const TABS = [
  { id: 'general', label: 'General' },
  { id: 'team', label: 'Team' },
  { id: 'sites', label: 'Sites' },
  { id: 'hash-frag', label: 'URL fragments (#)' },
  // TODO(settings-IA): Share links are site-scoped (need propertyId). Jul 23
  // meetings + docs/GUIDANCE_SYSTEM_PLAN.md §8 — move to Site settings; don't
  // present as workspace-global. Keep until next settings pass.
  { id: 'share-links', label: 'Share links' },
] as const

type TabId = (typeof TABS)[number]['id']

export default function WorkspaceSettings() {
  const ctx = useOutletContext<DashboardContext>()
  const { workspace, properties, isLoading: workspaceLoading, refetch } = useWorkspace()
  const { token } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [settings, setSettings] = useState<WorkspaceSettingsType>(defaultWorkspaceSettings)
  const [workspaceName, setWorkspaceName] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [activeTab, setActiveTab] = useState<TabId>('general')
  // Agent plans have one seat and no workspace members.
  const { isAgentPlan } = useSubscription()
  const tabs = isAgentPlan ? TABS.filter((tab) => tab.id !== 'team') : TABS
  const [settingsDirty, setSettingsDirty] = useState(false)
  const [nameDirty, setNameDirty] = useState(false)

  const isLoading = ctx.isLoading || workspaceLoading
  const isDirty = settingsDirty || nameDirty

  useEffect(() => {
    if (!workspace) return
    setWorkspaceName(workspace.name || '')
    setNameDirty(false)
    if (workspace.settings && typeof workspace.settings === 'object') {
      setSettings({ ...defaultWorkspaceSettings, ...workspace.settings })
    }
    setSettingsDirty(false)
    // Re-hydrate only when switching workspaces — not on every refetch mutation.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional workspaceId gate
  }, [workspace?.workspaceId])

  const handleSettingsChange = (partialSettings: Partial<WorkspaceSettingsType>) => {
    setSettings((prev) => ({ ...prev, ...partialSettings }))
    setSettingsDirty(true)
  }

  const handleNameChange = (name: string) => {
    setWorkspaceName(name)
    setNameDirty(name.trim() !== (workspace?.name || ''))
  }

  const handleSave = async () => {
    if (!workspace?.workspaceId || !token) return
    setIsSaving(true)
    try {
      const updates: Record<string, unknown> = {}
      if (workspaceName.trim() && workspaceName.trim() !== workspace.name) {
        updates.name = workspaceName.trim()
      }
      // Only send settings when they've been explicitly changed — avoids a 500 if
      // the settings column migration hasn't been applied on the backend yet
      if (settingsDirty) {
        // Strip shareLinks/ipFilters — those are managed by separate endpoints
        const settingsForPut = { ...settings }
        delete (settingsForPut as { shareLinks?: unknown }).shareLinks
        delete (settingsForPut as { ipFilters?: unknown }).ipFilters
        updates.settings = settingsForPut as unknown as Record<string, unknown>
      }
      await updateWorkspace(workspace.workspaceId, updates, token)
      setSettingsDirty(false)
      setNameDirty(false)
      refetch()
      toast.success('Settings saved')
    } catch {
      toast.error("Couldn't save settings")
    } finally {
      setIsSaving(false)
    }
  }

  const header = (
    <PageHeader
      title="Workspace settings"
      description={`Team, sites and tracking defaults shared by every site in ${workspace?.name || 'this workspace'}.`}
      className="mb-0"
    />
  )

  if (isLoading) {
    return (
      <PageShell maxWidth="max-w-none">
        {header}
        <SettingsTabs label="Workspace settings" tabs={tabs} active={activeTab} onChange={setActiveTab} />
        <SettingsBody>
          <div className="well w-full max-w-[784px]">
            <div className="surface-raised flex flex-col gap-4 p-6">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
            </div>
          </div>
        </SettingsBody>
      </PageShell>
    )
  }

  return (
    <WorkspaceTeamProvider>
      <PageShell maxWidth="max-w-none">
        {header}
        <SettingsTabs label="Workspace settings" tabs={tabs} active={activeTab} onChange={setActiveTab} />

        <SettingsBody>
          {activeTab === 'general' && (
            <GeneralSettingsTab
              workspaceName={workspaceName || workspace?.name || 'Untitled Workspace'}
              workspaceId={workspace?.workspaceId}
              onNameChange={handleNameChange}
              settings={settings}
              onSettingsChange={handleSettingsChange}
              onSave={handleSave}
              isSaving={isSaving}
              isDirty={isDirty}
              siteCount={properties?.length || 0}
              onDeleteWorkspace={async () => {
                if (!workspace?.workspaceId || !token) return
                try {
                  await deleteWorkspace(workspace.workspaceId, token)
                  // The switcher and Account settings cache this list for 5 min;
                  // without this the deleted workspace stays selectable.
                  await queryClient.invalidateQueries({ queryKey: ['workspaces'] })
                  toast.success('Workspace deleted')
                  navigate('/dashboard')
                } catch (error: unknown) {
                  const message =
                    error instanceof Error ? error.message : 'Failed to delete workspace'
                  toast.error(message)
                }
              }}
            />
          )}

          {activeTab === 'team' && !isAgentPlan && <TeamSettingsTab />}

          {activeTab === 'sites' && <WorkspaceSitesTab />}

          {activeTab === 'hash-frag' && (
            <HashFragSettingsTab
              settings={settings}
              onSettingsChange={handleSettingsChange}
              onSave={handleSave}
              isSaving={isSaving}
              isDirty={settingsDirty}
            />
          )}

          {activeTab === 'share-links' && <ShareLinksSettingsTab />}
        </SettingsBody>
      </PageShell>
    </WorkspaceTeamProvider>
  )
}
